import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { EmptyState, ErrorState, SkeletonRows } from "../primitives/Feedback";

export interface DataColumn<T> {
  key: string;
  header: string;
  /** Rendered in both presentations. */
  cell: (row: T) => ReactNode;
  /** Right-align and use tabular figures -- money and counts. */
  numeric?: boolean;
  /** Hidden on the phone card; the table still shows it. */
  desktopOnly?: boolean;
  /** Promoted to the card title on a phone. Exactly one column should set this. */
  primary?: boolean;
}

export interface DataListProps<T> {
  columns: Array<DataColumn<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  error?: string | null;
  empty?: { title: string; description?: string; action?: ReactNode };
  /**
   * Row actions. Rendered in *both* presentations -- footer of the phone card,
   * trailing cell of the table. They used to be card-only, which quietly left
   * every action unreachable on a laptop.
   */
  rowActions?: (row: T) => ReactNode;
}

/**
 * One data set, two presentations, one definition.
 *
 * A phone gets a stack of cards; `md` and up gets a table. This is the whole
 * reason the component exists: the alternative -- a table in a horizontal
 * scroller -- is what makes an admin tool unusable on the device people actually
 * carry, and these consoles have no mobile app to fall back on.
 */
export function DataList<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  error,
  empty,
  rowActions,
}: DataListProps<T>) {
  if (loading) return <SkeletonRows rows={4} />;
  if (error) return <div className="p-4"><ErrorState message={error} /></div>;
  if (rows.length === 0) {
    return (
      <EmptyState
        title={empty?.title ?? "Nothing here yet"}
        description={empty?.description}
        action={empty?.action}
      />
    );
  }

  const primary = columns.find((column) => column.primary) ?? columns[0];
  const secondary = columns.filter((column) => column !== primary && !column.desktopOnly);

  return (
    <>
      <ul className="divide-y divide-[var(--border-subtle)] md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)}>
            <div
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "px-4 py-3",
                onRowClick && "cursor-pointer active:bg-[var(--surface-sunken)]",
              )}
            >
              <div className="text-sm font-medium text-[var(--text-primary)]">
                {primary ? primary.cell(row) : null}
              </div>
              {secondary.length > 0 && (
                <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
                  {secondary.map((column) => (
                    <div key={column.key} className="min-w-0">
                      <dt className="text-2xs uppercase tracking-wider text-[var(--text-muted)]">
                        {column.header}
                      </dt>
                      <dd
                        className={cn(
                          "truncate text-sm text-[var(--text-secondary)]",
                          column.numeric && "numeric",
                        )}
                      >
                        {column.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {rowActions && <div className="mt-2 flex flex-wrap gap-2">{rowActions(row)}</div>}
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-subtle)]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "px-4 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-[var(--text-muted)]",
                    column.numeric && "text-right",
                  )}
                >
                  {column.header}
                </th>
              ))}
              {rowActions && (
                <th scope="col" className="px-4 py-2 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-subtle)]">
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  onRowClick && "cursor-pointer hover:bg-[var(--surface-sunken)]",
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    data-numeric={column.numeric ? "true" : undefined}
                    className={cn(
                      "px-4 py-2.5 align-middle text-[var(--text-primary)]",
                      column.numeric && "text-right",
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-4 py-2.5 align-middle">
                    {/* Stops a row-level onRowClick from firing when an action inside it is pressed. */}
                    <div
                      className="flex flex-wrap items-center justify-end gap-2"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {rowActions(row)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
